import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Abastecimento, Veiculo, Motorista } from '../../types';
import { abastecimentosService, AbastecimentoResponse } from '../../services/abastecimentos';
import { veiculosService } from '../../services/veiculos';
import { motoristasService } from '../../services/motoristas';
import UploadDeArquivos from '../../components/UploadDeArquivos';
import CurrencyInput from '../../components/CurrencyInput';
import { KmInput, LitrosInput } from '../../components/MaskedInputs';
import CampoCidadeUf from '../../components/CampoCidadeUf';
import SearchableSelect from '../../components/SearchableSelect';
import DicaUltimoKm from '../../components/DicaUltimoKm';
import { erroDeKmMenor, useUltimoKm } from '../../hooks/useUltimoKm';

// Listas de seleção precisam vir completas: o SearchableSelect filtra em memória,
// então o que não veio na resposta é invisível para a busca do usuário.
const OPCOES_SELECT = { per_page: 500 } as const;

const AbastecimentosForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    veiculo_id: '',
    motorista_id: '',
    data_abastecimento: new Date().toISOString().split('T')[0],
    quilometragem: '',
    litros: '',
    preco_litro: '',
    valor_total: '',
    tipo_combustivel: 'gasolina',
    posto: '',
    cidade: '',
    estado: '',
    consumo: '',
    km_rodados: '',
    tanque_cheio: false,
    observacoes: '',
    nota_fiscal: '',
    comprovantes: [] as string[]
  });

  // Buscar dados do abastecimento se estiver editando
  const { data: abastecimentoData, isLoading: isLoadingAbastecimento } = useQuery<AbastecimentoResponse>({
    queryKey: ['abastecimento', id],
    queryFn: () => abastecimentosService.getAbastecimento(Number(id!)),
    enabled: isEditing,
    staleTime: 0
  });

  // Buscar veículos
  const { data: veiculosData } = useQuery({
    queryKey: ['veiculos', OPCOES_SELECT],
    queryFn: () => veiculosService.getVeiculos(OPCOES_SELECT),
    placeholderData: (previousData) => previousData
  });

  // Buscar motoristas
  const { data: motoristasData } = useQuery({
    queryKey: ['motoristas', OPCOES_SELECT],
    queryFn: () => motoristasService.getMotoristas(OPCOES_SELECT),
    placeholderData: (previousData) => previousData
  });

  // Carregar dados do abastecimento para edição
  useEffect(() => {
    if (abastecimentoData?.data) {
      const abastecimento = abastecimentoData.data;
      setFormData({
        veiculo_id: abastecimento.veiculo_id?.toString() ?? '',
        motorista_id: abastecimento.motorista_id?.toString() ?? '',
        data_abastecimento: abastecimento.data_abastecimento?.split('T')[0] ?? '',
        quilometragem: abastecimento.quilometragem?.toString() ?? '',
        litros: abastecimento.litros?.toString() ?? '',
        preco_litro: abastecimento.preco_litro?.toString() ?? '',
        valor_total: abastecimento.valor_total?.toString() ?? '',
        tipo_combustivel: abastecimento.tipo_combustivel ?? 'gasolina',
        posto: abastecimento.posto ?? '',
        cidade: abastecimento.cidade ?? '',
        estado: abastecimento.estado ?? '',
        consumo: abastecimento.consumo?.toString() ?? '',
        km_rodados: abastecimento.km_rodados?.toString() ?? '',
        tanque_cheio: abastecimento.tanque_cheio ?? false,
        observacoes: abastecimento.observacoes ?? '',
        nota_fiscal: abastecimento.nota_fiscal ?? '',
        comprovantes: abastecimento.comprovantes ?? []
      });
    }
  }, [abastecimentoData]);

  // Mutation para criar/atualizar abastecimento
  const mutation = useMutation({
    mutationFn: (data: Partial<Abastecimento>) => {
      if (isEditing) {
        return abastecimentosService.updateAbastecimento(Number(id!), data);
      } else {
        return abastecimentosService.createAbastecimento(data);
      }
    },
    onSuccess: (response) => {
      if (response.success) {
        toast.success(isEditing ? 'Abastecimento atualizado com sucesso!' : 'Abastecimento registrado com sucesso!');
        // Invalidar so a lista deixava o detalhe e o proprio formulario
        // servindo o cache anterior a edicao: o registro aparecia atualizado na
        // listagem e desatualizado ao abrir. Sao chaves diferentes, e as duas
        // precisam cair.
        queryClient.invalidateQueries({ queryKey: ['abastecimentos'] });
        if (id) {
          queryClient.invalidateQueries({ queryKey: ['abastecimento', id] });
        }
        queryClient.invalidateQueries({ queryKey: ['veiculos'] });
        navigate('/abastecimentos');
      } else {
        toast.error(response.message || 'Erro ao salvar abastecimento');
      }
    },
    onError: (error: any) => {
      toast.error('Erro ao salvar abastecimento');
      console.error('Erro:', error);
    }
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      };

      // Calcular valor total automaticamente
      if (name === 'litros' || name === 'preco_litro') {
        const litros = name === 'litros' ? parseFloat(value) : parseFloat(newData.litros);
        const preco = name === 'preco_litro' ? parseFloat(value) : parseFloat(newData.preco_litro);
        
        if (!isNaN(litros) && !isNaN(preco) && litros > 0 && preco > 0) {
          newData.valor_total = (litros * preco).toFixed(2);
        }
      }

      // Calcular consumo automaticamente
      if (name === 'km_rodados' || name === 'litros') {
        const km = name === 'km_rodados' ? parseFloat(value) : parseFloat(newData.km_rodados);
        const litros = name === 'litros' ? parseFloat(value) : parseFloat(newData.litros);
        
        if (!isNaN(km) && !isNaN(litros) && km > 0 && litros > 0) {
          newData.consumo = (km / litros).toFixed(2);
        }
      }

      return newData;
    });
  };

  // O ultimo km do painel do veiculo escolhido: vem preenchido na criacao e e
  // o piso do que pode ser digitado. O servidor recusa igual.
  const { ultimo: ultimoKm, carregando: buscandoKm } = useUltimoKm(formData.veiculo_id);

  useEffect(() => {
    if (isEditing || !ultimoKm) return;
    setFormData((prev: typeof formData) =>
      prev.quilometragem === '' ? { ...prev, quilometragem: String(Math.round(ultimoKm.km)) } : prev,
    );
  }, [ultimoKm, isEditing]);

  const erroKm = erroDeKmMenor(formData.quilometragem, ultimoKm);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Na edicao o proprio registro pode ser o ultimo km; quem decide e o
    // servidor, que sabe ignora-lo. Na criacao a regra vale inteira aqui.
    if (!isEditing && erroKm) {
      toast.error(erroKm);
      return;
    }

    // Validações básicas
    if (!formData.veiculo_id || !formData.motorista_id) {
      toast.error('Selecione o veículo e o motorista');
      return;
    }

    if (!formData.litros || !formData.preco_litro) {
      toast.error('Preencha os campos de litros e preço por litro');
      return;
    }

    // Obrigatorio no cadastro, sem leitura do conteudo: basta um anexo. Na
    // edicao nao, para nao travar a correcao de lancamento antigo sem anexo.
    if (!isEditing && formData.comprovantes.length === 0) {
      toast.error('É obrigatório anexar pelo menos um comprovante.');
      return;
    }

    // Preparar dados para envio
    const submitData: Partial<Abastecimento> = {
      veiculo_id: Number(formData.veiculo_id),
      motorista_id: Number(formData.motorista_id),
      data_abastecimento: formData.data_abastecimento,
      quilometragem: Number(formData.quilometragem),
      litros: Number(formData.litros),
      preco_litro: Number(formData.preco_litro),
      valor_total: Number(formData.valor_total),
      tipo_combustivel: formData.tipo_combustivel as 'gasolina' | 'etanol' | 'diesel' | 'gnv' | 'flex',
      posto: formData.posto,
      cidade: formData.cidade,
      estado: formData.estado,
      tanque_cheio: formData.tanque_cheio,
      observacoes: formData.observacoes || null,
      nota_fiscal: formData.nota_fiscal || null,
      comprovantes: formData.comprovantes.length > 0 ? formData.comprovantes : null,
    };

    // Adicionar campos opcionais se preenchidos
    if (formData.consumo) {
      submitData.consumo = Number(formData.consumo);
    }
    if (formData.km_rodados) {
      submitData.km_rodados = Number(formData.km_rodados);
    }

    mutation.mutate(submitData);
  };


  if (isLoadingAbastecimento) {
    return (
      <div className="w-full">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Editar abastecimento' : 'Novo abastecimento'}
          </h1>
          <p className="text-gray-600">
            {isEditing ? 'Atualize as informações do abastecimento' : 'Registre um novo abastecimento'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4">Informações Básicas</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Veículo */}
              <SearchableSelect
                label="Veículo"
                required
                placeholder="Selecione o veículo"
                value={formData.veiculo_id}
                onChange={(v) => setFormData(prev => ({ ...prev, veiculo_id: v }))}
                options={(veiculosData?.data ?? []).map((veiculo: Veiculo) => ({
                  value: String(veiculo.id),
                  label: `${veiculo.placa} - ${veiculo.marca} ${veiculo.modelo}`,
                }))}
              />

              {/* Motorista */}
              <SearchableSelect
                label="Motorista"
                required
                placeholder="Selecione o motorista"
                value={formData.motorista_id}
                onChange={(v) => setFormData(prev => ({ ...prev, motorista_id: v }))}
                options={(motoristasData?.data ?? []).map((motorista: Motorista) => ({
                  value: String(motorista.id),
                  label: `${motorista.nome} - ${motorista.cnh}`,
                }))}
              />

              {/* Data do Abastecimento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data do Abastecimento <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="data_abastecimento"
                  value={formData.data_abastecimento}
                  onChange={handleInputChange}
                  required
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Quilometragem: vem preenchida com o ultimo km do painel. */}
              <div>
                <KmInput
                  label="Quilometragem"
                  required
                  name="quilometragem"
                  value={formData.quilometragem}
                  onChange={handleInputChange}
                />
                <DicaUltimoKm ultimo={ultimoKm} erro={isEditing ? null : erroKm} carregando={buscandoKm} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4">Informações do Combustível</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Tipo de Combustível */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Combustível <span className="text-red-500">*</span>
                </label>
                <select
                  name="tipo_combustivel"
                  value={formData.tipo_combustivel}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="gasolina">Gasolina</option>
                  <option value="etanol">Etanol</option>
                  <option value="diesel">Diesel</option>
                  <option value="gnv">GNV</option>
                  <option value="flex">Flex</option>
                </select>
              </div>

              {/* Litros */}
              <LitrosInput
                label="Litros"
                required
                name="litros"
                ajuda="Use vírgula para os decimais, como está na bomba: 112,676"
                value={formData.litros}
                onChange={handleInputChange}
              />

              {/* Preço por Litro */}
              <CurrencyInput
                name="preco_litro"
                value={formData.preco_litro}
                onChange={handleInputChange}
                label="Preço por Litro"
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {/* Valor Total */}
              <div>
                <CurrencyInput
                  name="valor_total"
                  value={formData.valor_total}
                  onChange={handleInputChange}
                  label="Valor Total"
                  disabled
                  className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-600"
                />
                <p className="text-xs text-gray-500 mt-1">Calculado automaticamente</p>
              </div>

              {/* Tanque Cheio */}
              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="tanque_cheio"
                    checked={formData.tanque_cheio}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Tanque cheio</span>
                </label>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4">Informações do Posto</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Posto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Posto <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="posto"
                  value={formData.posto}
                  onChange={handleInputChange}
                  required
                  placeholder="Ex: Posto Shell"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Cidade e UF: a lista de municipios vem do IBGE, para o mesmo
                  posto nao virar "Macapa", "MACAPÁ" e "macapa" no relatorio. */}
              <CampoCidadeUf
                uf={formData.estado}
                cidade={formData.cidade}
                required
                onChange={({ uf, cidade }) => setFormData((prev) => ({ ...prev, estado: uf, cidade }))}
              />

              {/* Nota Fiscal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número da Nota Fiscal
                </label>
                <input
                  type="text"
                  name="nota_fiscal"
                  value={formData.nota_fiscal}
                  onChange={handleInputChange}
                  placeholder="Ex: 123456"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4">Informações Adicionais</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* KM Rodados */}
              <KmInput
                label="KM Rodados"
                name="km_rodados"
                value={formData.km_rodados}
                onChange={handleInputChange}
              />

              {/* Consumo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Consumo (km/L)
                </label>
                <input
                  type="text"
                  value={formData.consumo ? `${formData.consumo} km/L` : ''}
                  readOnly
                  className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-600"
                />
                <p className="text-xs text-gray-500 mt-1">Calculado automaticamente</p>
              </div>

              {/* Comprovantes: cupom fiscal e comprovante de pagamento costumam
                  vir separados, entao aceita mais de um. */}
              <div className="md:col-span-2">
                <UploadDeArquivos
                  label="Comprovantes do abastecimento"
                  obrigatorio={!isEditing}
                  valor={formData.comprovantes}
                  onChange={(comprovantes) => setFormData(prev => ({ ...prev, comprovantes }))}
                  accept=".jpg,.jpeg,.png,.pdf"
                />
              </div>

              {/* Observações */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Observações adicionais sobre o abastecimento..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/abastecimentos')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mutation.isPending ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Registrar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AbastecimentosForm;
